import type { ReactNode } from 'react';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { links, resolveFormattingLocale } from '@eis/core';
import { DetailsSection, usePermissions } from '@eis/patterns';
import { insurancePermissions as p } from '../../../permissions';
import type { PartnerRoleRule } from '../../policies/model/partners';
import { roleNameOf } from '../../policies/model/partners';
import { PartnerList } from '../../policies/ui/PartnerList';
import { LeadPremiumSection, PremiumSection } from '../../premium/ui/PremiumSection';
import {
  draftPartnerLabel,
  flattenData,
  isMemberLead,
  moneyLabel,
  tarifNameOf,
} from '../model/leads';
import type { Lead } from '../model/types';

/**
 * A lead's body, read-only, in the policy's own sections (SDD-013 §5.1: `draft` IS a policy, so
 * it is rendered with the policy components unchanged) — the Overview tab, and the History tab
 * for any recorded version. `changed` marks the rows that differ from the previous version.
 *
 * The parties section adds the one thing a policy never has: partners that are not registered
 * yet, shown in their role with a "not registered" chip and the draft's own fields (§5.3).
 */
export function LeadDraftSections({
  lead,
  rules,
  changed,
}: {
  readonly lead: Lead;
  readonly rules: readonly PartnerRoleRule[] | undefined;
  /**
   * A recorded version shown against the one before it (the History tab): the changed fields are
   * marked, and its premium is shown as recorded — no estimate, which is a question about now.
   */
  readonly changed?: ReadonlySet<string>;
}): ReactNode {
  const { t, i18n } = useTranslation('insurance');
  const locale = resolveFormattingLocale(i18n.language);
  const navigate = useNavigate();
  const { can } = usePermissions();
  const draft = lead.draft;
  const member = isMemberLead(lead);
  const marker = (key: string): ReactNode =>
    changed?.has(key) === true ? (
      <Tooltip title={t('leads.history.changedHint')}>
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label={t('leads.history.changedMarker')}
          data-testid="changed-marker"
        />
      </Tooltip>
    ) : null;
  const withMarker = (key: string, value: ReactNode): ReactNode => (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      <span>{value}</span>
      {marker(key)}
    </Stack>
  );
  const data = flattenData(draft?.data);
  const parentCode = draft?.parentPolicyCode ?? '';

  return (
    <Stack spacing={2.5}>
      <DetailsSection
        title={t('leads.overview.parties')}
        fields={[
          ...(member
            ? [
                {
                  label: t('leads.overview.policyholder'),
                  value: (
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <span>{draft?.policyholder?.resolvedPartnerId ?? '—'}</span>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t('policies.list.inherited')}
                        data-testid="inherited-marker"
                      />
                    </Stack>
                  ),
                },
                {
                  label: t('leads.overview.groupPolicy'),
                  value:
                    parentCode !== '' && can(p.groupPolicies.view) ? (
                      <Link
                        href={links.insurance.groupPolicy(parentCode, 'leads')}
                        onClick={(event) => {
                          event.preventDefault();
                          void navigate({ href: links.insurance.groupPolicy(parentCode, 'leads') });
                        }}
                      >
                        {parentCode}
                      </Link>
                    ) : (
                      parentCode
                    ),
                },
              ]
            : []),
          {
            label: t('leads.overview.partiesPerRole'),
            wide: true,
            value: withMarker('partners', <PartnerList partners={draft?.partners} rules={rules} />),
          },
          ...((lead.partnerDrafts ?? []).length > 0
            ? [
                {
                  label: t('leads.overview.drafts'),
                  wide: true,
                  value: (
                    <Stack spacing={0.5} data-testid="partner-drafts">
                      {(lead.partnerDrafts ?? []).map((partnerDraft, index) => {
                        const label = draftPartnerLabel(partnerDraft);
                        return (
                          <Stack
                            key={`${partnerDraft.roleErn ?? ''}-${String(index)}`}
                            direction="row"
                            spacing={1}
                            sx={{ alignItems: 'center' }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              {roleNameOf(partnerDraft.roleErn ?? '', rules)}
                            </Typography>
                            <Typography variant="body2">
                              {label.name === '' ? '—' : label.name}
                            </Typography>
                            {label.typeCode !== '' && (
                              <Typography variant="caption" color="text.secondary">
                                {t(`leads.create.draftTypes.${label.typeCode}` as never, {
                                  defaultValue: label.typeCode,
                                })}
                              </Typography>
                            )}
                            <Tooltip title={t('leads.list.notRegisteredHint')}>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={t('leads.list.notRegistered')}
                                data-testid="draft-marker"
                              />
                            </Tooltip>
                            {marker('partnerDrafts')}
                          </Stack>
                        );
                      })}
                    </Stack>
                  ),
                },
              ]
            : []),
        ]}
      />

      <DetailsSection
        title={t('leads.overview.cover')}
        fields={[
          // The premium itself is the *Prämie* section below (SDD-021 §9); a recorded version
          // still marks here that its premium differs from the version before.
          ...(changed?.has('premium.monthly') === true
            ? [
                {
                  label: t('leads.overview.premium'),
                  value: withMarker('premium.monthly', moneyLabel(draft?.premium?.monthly, locale)),
                },
              ]
            : []),
          // The product by NAME — the configuration as a name, never as coordinates (D-C, SSOT I-6).
          {
            label: t('leads.overview.tarif'),
            value: withMarker(
              'configuration',
              tarifNameOf(draft?.configuration?.resolvedTarifVersionErn),
            ),
          },
          {
            label: t('leads.header.effectiveFrom'),
            value: withMarker('effectiveFrom', lead.effectiveFrom ?? '—'),
          },
        ]}
        advancedFields={[
          {
            label: t('leads.overview.configuration'),
            value: draft?.configuration?.resolvedTarifVersionErn ?? '—',
          },
          { label: t('policies.overview.digest'), value: draft?.configuration?.digest ?? '—' },
        ]}
      />

      {/*
        *Tatsächlich* is the draft's priced premium, *Geschätzt* the refresh preview — on a stale pin
        the refreshed version's price, the numbers the accept dialog shows (SDD-021 §9, U-4).
      */}
      {changed === undefined ? (
        <LeadPremiumSection
          code={lead.code ?? ''}
          premium={draft?.premium}
          {...(member ? { groupPolicyCode: parentCode } : {})}
          locale={locale}
        />
      ) : (
        <PremiumSection
          premium={draft?.premium}
          estimate={{ status: 'absent' }}
          {...(member ? { groupPolicyCode: parentCode } : {})}
          locale={locale}
        />
      )}

      <DetailsSection
        title={t('leads.overview.data')}
        fields={Object.entries(data).map(([key, value]) => ({
          label: key,
          value: withMarker(`data.${key}`, value),
        }))}
      />
    </Stack>
  );
}
